/**
 * TimerDisplay - Shared timer visualization component
 *
 * Displays a circular progress timer used in PreK assessments
 */

import React from 'react';
import { TIMER_CONSTANTS } from '../preKConstants';

interface TimerDisplayProps {
  timeRemaining: number;
  maxTime?: number;
  isRunning: boolean;
  isTTSPlaying?: boolean;
  opacity?: number;
  hideProgress?: boolean;
  onClick?: () => void;
}

const TimerDisplay: React.FC<TimerDisplayProps> = ({
  timeRemaining,
  maxTime = TIMER_CONSTANTS.WALL_CLOCK_LIMIT_MS,
  isRunning,
  isTTSPlaying = false,
  opacity = 0.4,
  hideProgress = false,
  onClick,
}) => {
  // Circle calculations
  const radius = 14;
  const circumference = 2 * Math.PI * radius; // ~87.96
  const progress = 1 - timeRemaining / maxTime;
  const strokeDashoffset = circumference * progress;

  // Determine center icon
  const renderIcon = () => {
    if (onClick) {
      // Eye open / eye closed SVG for hideable timer
      return hideProgress ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    }
    const emoji = isTTSPlaying ? '🔊' : isRunning ? '⏱️' : '⏸️';
    return <span className="text-xs text-gray-400">{emoji}</span>;
  };

  return (
    <div
      className={`relative w-16 h-16${onClick ? ' cursor-pointer' : ''}`}
      style={{ opacity }}
      onClick={onClick}
    >
      <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 32 32">
        {/* Background circle */}
        <circle
          cx="16"
          cy="16"
          r={radius}
          fill="none"
          stroke="#d1d5db"
          strokeWidth="1.5"
        />
        {/* Progress circle */}
        {isRunning && !hideProgress && (
          <circle
            cx="16"
            cy="16"
            r={radius}
            fill="none"
            stroke="#9ca3af"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-100 ease-linear"
          />
        )}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {renderIcon()}
      </div>
    </div>
  );
};

export default TimerDisplay;
